import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@store/index";
import { ListOptionBase } from "@shared/components/List";
import { Country, ProfileAuditResponse, EmailChangeStatus } from "@myProfile/models";
import {
  AccountFundAccessState,
  ContactInformationState,
  UserRolesState,
  UserIFCsState,
  CountriesState,
  ProfileAuditState,
  SaveProfileState,
  SelfServiceRequestState,
  RemovalSaveState,
} from "./ducks";

export const accountFundAccessSelector = (state: RootState): AccountFundAccessState => state.accountFundAccess;

export const editStateSelector = (state: RootState): boolean => state.accountFundAccess.isEditMode;

export const pendingRemovalsSelector = (state: RootState): string[] => state.accountFundAccess.pendingRemovals;

export const pendingRemovalContextsSelector = (state: RootState) => state.accountFundAccess.pendingRemovalContexts;

export const removalSaveSelector = (state: RootState): RemovalSaveState => state.accountFundAccess.removalSave;

export const contactInformationSelector = (state: RootState): ContactInformationState => state.contactInformation;

export const userRolesSelector = (state: RootState): UserRolesState => state.accountFundAccess.userRoles;

// User IFCs selector - used to determine profile menu visibility
export const userIFCsSelector = (state: RootState): UserIFCsState => state.accountFundAccess.userIFCs;

// Countries selectors
export const countriesStateSelector = (state: RootState): CountriesState => state.contactInformation.countries;

const countriesDataSelector = (state: RootState): Country[] => state.contactInformation.countries.data;

export const countriesOptionsSelector = createSelector(countriesDataSelector, (countries): ListOptionBase[] => {
  return countries
    .filter((country) => country && country.id && country.name)
    .map((country) => ({
      id: String(country.id),
      label: String(country.name),
    }));
});

// Profile Audit selectors
export const profileAuditSelector = (state: RootState): ProfileAuditState => state.contactInformation.profileAudit;

export const profileAuditDataSelector = (state: RootState): ProfileAuditResponse | null =>
  state.contactInformation.profileAudit.data;

// Pending email selector - extracts pending email info from audit data
// Reconciliation logic:
//   "Pending"  → show old email + Pending Update tag (email edit disabled)
//   "Approved" → show new email, no tag (case approved but not yet closed)
//   "Rejected" → case rejected by SF (AC7–AC9), no tag, fall back to Snowflake
//   "Closed"   → case fully resolved, no tag, fall back to Snowflake
//   null       → no email change requested, no tag
export interface PendingEmailInfo {
  hasPendingEmail: boolean;
  currentEmail: string | null;
  requestedEmail: string | null;
  emailChangeStatus: EmailChangeStatus | null;
}

export const pendingEmailSelector = createSelector(profileAuditDataSelector, (auditData): PendingEmailInfo => {
  if (!auditData) {
    return {
      hasPendingEmail: false,
      currentEmail: null,
      requestedEmail: null,
      emailChangeStatus: null,
    };
  }

  // Only show Pending tag when emailChangeStatus is explicitly "Pending"
  // Approved / Closed / Rejected / null → no pending tag
  const isPending = auditData.emailChangeStatus === "Pending";

  return {
    hasPendingEmail: isPending,
    currentEmail: auditData.currentEmail,
    requestedEmail: auditData.requestedEmail,
    emailChangeStatus: auditData.emailChangeStatus,
  };
});

// Save Profile selectors
export const saveProfileStateSelector = (state: RootState): SaveProfileState => state.contactInformation.saveProfile;

// LPC-10991: In-flight selectors for Self Service Reset Password / Reset MFA pop-ups.
// Used by the dialogs to disable the action button and render the loading spinner
// while the underlying Auth0 / Entitlements request is in progress.
export const resetPasswordStateSelector = (state: RootState): SelfServiceRequestState =>
  state.accountFundAccess.resetPassword;

export const resetMFAStateSelector = (state: RootState): SelfServiceRequestState => state.accountFundAccess.resetMFA;

// Selector to create selected country option for dropdown
export const createSelectedCountrySelector = (countryName: string | null) =>
  createSelector(countriesOptionsSelector, (countriesOptions): ListOptionBase | undefined => {
    if (!countryName) return undefined;

    return countriesOptions.find((c) => c.label.toLowerCase() === countryName.toLowerCase());
  });
